import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { PortalSource } from '../../common/Constants';
import { useGetPlanDefinitions } from '../../hooks/useGetPlanDefinitions';
import { useGuestAcquisition } from '../../hooks/useGuestAcquisition';
import { useLagoon } from '../../hooks/useLagoon';
import { useLocation } from '../../hooks/useLocation';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import {
  buildJarvisTargetUrl,
  getPortalSource,
  getRedirectUrl,
  getSelectedPlan,
  saveLocationToStorage,
  savePlanDefinitionsToStorage,
  savePortalStateToStorage,
} from './GuestAcquisition.utils';

export const GuestAcquisition = () => {
  const { navigate } = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const {
    setIsNavigatingMaestro,
    setIsNavigatingFromJarvis,
    setRequestData,
    setResponseData,
  } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      setIsNavigatingMaestro: state.setIsNavigatingMaestro,
      setIsNavigatingFromJarvis: state.setIsNavigatingFromJarvis,
      setRequestData: state.setRequestData,
      setResponseData: state.setResponseData,
    }))
  );

  const acquisitionData = useLagoon('acquisition')?.();

  const [, getGuestAcquisition] = useGuestAcquisition({});

  const [, getLocation] = useLocation({});

  const [, getPlanDefinitions] = useGetPlanDefinitions({});

  useEffect(() => {
    const fetchData = async () => {
      if (!acquisitionData) return;
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const transactionId = urlParams.get('txnId');
        const upstreamEnv = urlParams.get('upstream');

        if (!transactionId) {
          throw new Error('Missing txnId parameter');
        }

        const result = await getGuestAcquisition({
          variables: { transactionId },
        });
        const responseData = result?.data?.getGuestAcquisition;

        if (responseData) {
          const portalSource = getPortalSource(
            acquisitionData,
            responseData.client
          );
          const isNavigatingToMaestro = portalSource === PortalSource.MAESTRO;
          const isNavigatingToJarvisPCP = portalSource === PortalSource.JARVIS;
          setRequestData({ ...responseData, upstreamEnv });
          setResponseData([]);
          setIsNavigatingMaestro(isNavigatingToMaestro);
          setIsNavigatingFromJarvis(isNavigatingToJarvisPCP);

          if (isNavigatingToJarvisPCP) {
            let locationFeature;
            if (responseData.zip) {
              const locationResult = await getLocation({
                variables: {
                  address: encodeURIComponent(responseData.zip),
                  countySearchEnabled: true,
                },
              });

              locationFeature = locationResult?.data?.location?.features?.[0];
              if (locationFeature) {
                saveLocationToStorage(locationFeature);
              }
            }

            const stateCode = locationFeature?.stateCode || '';
            const countyFipsCode = responseData.fipsCode || '';

            const planDefinitionsResult = await getPlanDefinitions({
              variables: {
                planYear: responseData.year || String(new Date().getFullYear()),
                lob: 'IFP',
                coverageType: 'M',
                stateCode,
                countyFipsCode,
                isDualYear: true,
                isEI: false,
                portalSource,
              },
            });
            const planDefinitionsData =
              planDefinitionsResult?.data?.getPlanDefinitions;
            savePlanDefinitionsToStorage(planDefinitionsData);

            const selectedPlan = getSelectedPlan(
              planDefinitionsData,
              responseData.clientProdCode
            );
            savePortalStateToStorage(selectedPlan, stateCode, 'IFP');

            navigate(
              buildJarvisTargetUrl(acquisitionData, responseData.client)
            );
          } else {
            const redirectUrl = getRedirectUrl(
              acquisitionData,
              responseData.client
            );
            if (redirectUrl) {
              const navigateUrl = `${redirectUrl}?planSelectionLob=MR&coverageType=M&chipValue=All`;
              navigate(navigateUrl);
            }
          }
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading)
    return (
      <Flex
        alignItems="center"
        css={{
          height: '100%',
        }}
        id="acquisition-loading"
        justify="center"
      >
        <LoadingSpinner
          ariaLoadingLabel="loading results"
          data-testid="loading-spinner"
          isLoading
          size="xl"
        />
      </Flex>
    );
  if (error) return <p>Error: {error}</p>;
  return null;
};
